import axios, { isAxiosError } from "axios";
import axiosInstance from "../axiosConfig";
import * as FingerprintJS from '@fingerprintjs/fingerprintjs'
import {
    ENDPOINT_AUTH, ENDPOINT_AUTH_COM, ENDPOINT_AUTH_DETAILS,
    ENDPOINT_AUTOLOGIN, ENDPOINT_COM_DETAILS, ENDPOINT_COM_DETAILS_IMG,
    ENDPOINT_DEVICE_VERIFY, ENDPOINT_LANDING_ICON, ENDPOINT_PASSPORT_COM,
    ENDPOINT_PASSPORT_COM_GET, ENDPOINT_SIGNOUT, ENDPOINT_UPLOAD_PROFILE,
    ENDPOINT_USER_UPDATE, ENDPOINT_DEVICES, ENDPOINT_DEVICE_REMOVE,
    ENDPOINT_DEVICE_STATUS, ENDPOINT_AUTH_REAUTHORIZE, ENDPOINT_AUTH_MFA,
    // MFA Endpoints
    ENDPOINT_MFA_SETTINGS_GET, ENDPOINT_MFA_SETTINGS_UPDATE, ENDPOINT_MFA_PIN_SET,
    // Password Change Endpoints
    ENDPOINT_USER_REQUEST_PASSWORD_OTP, ENDPOINT_USER_VERIFY_PASSWORD_OTP,
    ENDPOINT_USER_CONFIRM_PASSWORD_CHANGE, ENDPOINT_USER_DELETE_SELF,
    ENDPOINT_USER_SECURITY_ACTIVITY
} from "../../constants";

export type CompanyData = {
    id: string
    name: string
    img: string
    owner: string
    device_approval?: boolean
    ip_whitelist?: boolean
    // Core1 only: lets its admins list users of any company.
    cross_com_browse?: boolean
}

export type AuthResponse = {
    companies: CompanyData[]
    code: string
    app: string
    applink?: string
    otpref?: string
    retries_left?: number // retries left before it will be blocked
}

export type LoginMfaChallenge = {
    token: string
    methods: string[]
    uname: string
    dis: string
}

export type AuthData = {
    Code: string
    Msg: AuthResponse | MfaSettingsResponse | LoginMfaChallenge
}

export type SessionDetails = {
    uname: string
    uid: string
    com: string
    dis: string
    expire: number
    roles: string[]
    session: string
    avatar: string
    lname: string
    fname: string
    email: string
    com_name: string
}

export type DeviceData = {
    id: string;
    name: string;
    type: string;
    last_accessed: string; // ISO date string for backward compatibility
    last_accessed_unix?: number; // Unix timestamp for sorting
    status: string; // Add status field
}

// Define the type for the response from the fetchUserDevices endpoint
export type FetchDevicesResponse = {
    devices: DeviceData[];
    current_device_id: string;
}

// MFA API Types
export type MfaSettingsResponse = {
    dis: string;
    avatar: string;
    is_mfa_enabled: boolean;
    is_pin_enabled: boolean;
    is_biometrics_enabled: boolean;
    is_passkey_enabled: boolean;
    has_pin_setup: boolean;
    has_passkey_setup: boolean;
}

export type ApiSuccessResponse<T> = {
    Code: string; // "200" for success
    Msg: T;
}

export type ApiErrorResponse = {
    Code: any; // Error code string
    Msg: string;  // Error message string
}


export async function login(client: string, username: string, pass: string, redir: string): Promise<AuthData> {
    try {
        const fpPromise = FingerprintJS.load();
        const fp = await fpPromise;
        const result = await fp.get();
        const visitorId = result.visitorId;

        const response = await axiosInstance.post(ENDPOINT_AUTH, {
            uname: btoa(username),
            pass: btoa(pass),
            client: client,
            redirect: redir,
            dcd: btoa(visitorId)
        }, { withCredentials: true });

        console.log(response.data);
        return response.data;
    } catch (err) {
        console.error(err);
        throw err;
    }
}

// MFA API Functions

export async function getMfaSettingsApi(): Promise<ApiSuccessResponse<MfaSettingsResponse> | ApiErrorResponse> {
    try {
        const response = await axiosInstance.get(ENDPOINT_MFA_SETTINGS_GET, { withCredentials: true });
        console.log(response.data)
        return response.data;
    } catch (err: any) {
        console.error("getMfaSettingsApi error:", err);
        if (isAxiosError(err) && err.response) {
            return err.response.data as ApiErrorResponse;
        }
        throw err; // Re-throw if it's not an Axios error with a response
    }
}

export async function updateMfaSettingsApi(payload: { enable_mfa?: boolean; enable_pin?: boolean; enable_passkey?: boolean; password: string }): Promise<ApiSuccessResponse<string> | ApiErrorResponse> {
    try {
        // Base64 encode the password for security
        const requestPayload = {
            ...payload,
            password: btoa(payload.password)
        };
        const response = await axios.post(ENDPOINT_MFA_SETTINGS_UPDATE, requestPayload, { withCredentials: true });
        return response.data;
    } catch (err: any) {
        console.error("updateMfaSettingsApi error:", err);
        if (axios.isAxiosError(err) && err.response) {
            return err.response.data as ApiErrorResponse;
        }
        throw err;
    }
}

export async function setMpinApi(pin: string, password: string): Promise<ApiSuccessResponse<string> | ApiErrorResponse> {
    try {
        const response = await axios.post(ENDPOINT_MFA_PIN_SET, {
            pin,
            password: btoa(password) // Base64 encode the password
        }, { withCredentials: true });
        return response.data;
    } catch (err: any) {
        console.error("setMpinApi error:", err);
        if (axios.isAxiosError(err) && err.response) {
            return err.response.data as ApiErrorResponse;
        }
        throw err;
    }
}

export function logout(all?: boolean): Promise<string> {
    const url = all ? `${ENDPOINT_SIGNOUT}?all=true` : ENDPOINT_SIGNOUT;
    return new Promise<string>(async (resolve, reject) => {
        axios.post(url, {}, { withCredentials: true }).then(res => {
            console.log(res.data)
            resolve(res.data.Code)
        })
            .catch((err) => {
                console.log(err)
                reject(err)
            });
    })
}

export function autologin(client: string, redir: string): Promise<AuthData> {
    return new Promise<AuthData>(async (resolve, reject) => {
        axios.post(ENDPOINT_AUTOLOGIN, {
            redirect: redir,
            client: client
        }, { withCredentials: true }).then(res => {
            console.log(res.data)
            resolve(res.data)
        })
            .catch((err) => {
                console.log(err)
                reject(err)
            });
    })
}

export function retrieveLandingIcon(client: string): Promise<any> {
    return new Promise<AuthData>(async (resolve, reject) => {
        axios.get(ENDPOINT_LANDING_ICON + "?client=" + client).then(res => {
            console.log(res.data)
            resolve(res.data)
        })
            .catch((err) => {
                console.log(err)
                reject(err)
            });
    })

}

// if theres a session retrieve the details. if failed means need to login
export function retrieveSessionDetails(): Promise<any> {
    return new Promise<AuthData>(async (resolve, reject) => {
        axios.get(ENDPOINT_AUTH_DETAILS, { withCredentials: true }).then(res => {
            console.log(res.data)
            resolve(res.data)
        })
            .catch((err) => {
                console.log(err)
                reject(err)
            });
    })

}


export function uploadAvatar(img: File): Promise<any> {
    const formData = new FormData();
    formData.append('profile', img)
    formData.append('fname', img.name)
    return new Promise<string>(async (resolve, reject) => {
        axios.post(ENDPOINT_UPLOAD_PROFILE, formData, {
            withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
        }).then(res => {
            //console.log(res.data)
            resolve(res.data)
        })
            .catch((err) => {
                console.log(err)
                reject(err)
            });
    })
}

export function updateDetails(dis: string, lname: string, fname: string): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
        axios.post(ENDPOINT_USER_UPDATE, {
            dis: dis,
            lname: lname,
            fname: fname
        }, { withCredentials: true }).then(res => {
            console.log(res.data)
            resolve(res.data.Code)
        })
            .catch((err) => {
                console.log(err)
                reject(err)
            });
    })
}

export function setCompanyAuth(company: string, code: string, client: string): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
        axios.post(ENDPOINT_AUTH_COM, {
            com: company,
            code: code,
            client: client,
        }, { withCredentials: true }).then(res => {
            console.log(res.data)
            resolve(res.data.Code)
        })
            .catch((err) => {
                console.log(err)
                reject(err)
            });
    })
}

export function retrieveCompanies(): Promise<any> {
    return new Promise<string>(async (resolve, reject) => {
        axios.get(ENDPOINT_PASSPORT_COM_GET, { withCredentials: true }).then(res => {
            console.log(res.data)
            resolve(res.data)
        })
            .catch((err) => {
                console.log(err)
                reject(err)
            });
    })
}

export function fetchUserDevices(): Promise<FetchDevicesResponse> {
    return new Promise<FetchDevicesResponse>(async (resolve, reject) => {
        axios.get(ENDPOINT_DEVICES, { withCredentials: true }).then(res => {
            console.log(res.data);
            resolve(res.data); // Resolve with the entire response object
        })
            .catch((err) => {
                console.log(err);
                reject(err);
            });
    });
}

export function changeDeviceStatus(deviceId: string, status: string): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
        axios.post(ENDPOINT_DEVICE_STATUS, {
            id: deviceId,
            status: status,
        }, { withCredentials: true }).then(res => {
            console.log(res.data);
            resolve(res.data.Code); // Assuming backend returns a Code field for success/error
        })
            .catch((err) => {
                console.log(err);
                reject(err);
            });
    });
}

export function removeDevice(deviceId: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
        axios.post(ENDPOINT_DEVICE_REMOVE, {
            id: deviceId,
        }, { withCredentials: true }).then(res => {
            const data = res.data;
            if (data.Code === "200") {
                console.log("Device removed successfully:", data.Msg);
                resolve();
            } else {
                console.error("Device removal failed:", data.Code, data.Msg);
                reject(new Error(data.Msg || "Failed to remove device"));
            }
        })
            .catch((err) => {
                console.error("Device removal error:", err);
                reject(err);
            });
    });
}

export function setCompany(com: string): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
        axios.post(ENDPOINT_PASSPORT_COM, {
            com: com
        }, { withCredentials: true }).then(res => {
            console.log(res.data)
            resolve(res.data.Code)
        })
            .catch((err) => {
                console.log(err);
                reject(err);
            });
    });
}

export function retrieveComProfile(): Promise<any> {
    return new Promise<CompanyData>(async (resolve, reject) => {
        axios.get(ENDPOINT_COM_DETAILS, { withCredentials: true }).then(res => {
            console.log(res.data)
            resolve(res.data)
        })
            .catch((err) => {
                console.log(err)
                reject(err)
            });
    })

}


export function uploadComAvatar(img: File): Promise<any> {
    const formData = new FormData();
    formData.append('profile', img)
    formData.append('fname', img.name)
    return new Promise<string>(async (resolve, reject) => {
        axios.post(ENDPOINT_COM_DETAILS_IMG, formData, {
            withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
        }).then(res => {
            //console.log(res.data)
            resolve(res.data)
        })
            .catch((err) => {
                console.log(err)
                reject(err)
            });
    })
}

export function verifyDeviceOtpApi(refid: string, otp: string): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
        axios.post(ENDPOINT_DEVICE_VERIFY, { // Use the correct backend endpoint
            refid: refid,
            otp: otp,
        }, { withCredentials: true }).then(res => {
            console.log(res.data);
            resolve(res.data.Code);
        })
            .catch((err) => {
                console.log(err);
                reject(err);
            });
    });
}

export async function reauthenticate(redir: string, client: string, credentialValue: string, credentialType: string): Promise<any> {
    try {
        const response = await axios.post(ENDPOINT_AUTH_REAUTHORIZE, {
            credential_value: btoa(credentialValue), // MPIN should also be base64 encoded if password is
            credential_type: credentialType,
            client: client,
            redirect: redir
        }, { withCredentials: true });

        console.log(response.data);
        return response.data;
    } catch (err) {
        console.error(err);
        throw err;
    }
}

// Completes a login that was paused for a second factor. The `token` was issued by the
// password step (ERR_MFA_REQUIRED). credentialType is "mpin" or "passkey"; for passkey the
// value is the WebAuthn assertion JSON string.
export async function completeLoginMfaApi(token: string, credentialType: string, credentialValue: string): Promise<AuthData> {
    try {
        const response = await axios.post(ENDPOINT_AUTH_MFA, {
            token: token,
            credential_type: credentialType,
            credential_value: btoa(credentialValue)
        }, { withCredentials: true });
        console.log(response.data);
        return response.data;
    } catch (err) {
        console.error(err);
        throw err;
    }
}

// Password Change API Functions
export async function requestPasswordChangeOTP(): Promise<void> {
    try {
        const response = await axios.post(
            ENDPOINT_USER_REQUEST_PASSWORD_OTP,
            {},
            { withCredentials: true }
        );
        console.log(response.data);
        if (response.data.Code !== "200") {
            throw new Error(response.data.Msg || "Failed to send OTP");
        }
    } catch (err: any) {
        if (err.response?.data?.Msg) {
            throw new Error(err.response.data.Msg);
        }
        throw new Error("Failed to request OTP");
    }
}

export async function verifyPasswordChangeOTP(otp: string): Promise<string> {
    try {
        const response = await axios.post(
            ENDPOINT_USER_VERIFY_PASSWORD_OTP,
            { otp },
            { withCredentials: true }
        );

        if (response.data.Code !== "200" || !response.data.Msg?.token) {
            throw new Error(response.data.Msg || "Failed to verify OTP");
        }

        return response.data.Msg.token;
    } catch (err: any) {
        if (err.response?.data?.Msg) {
            throw new Error(err.response.data.Msg);
        }
        throw new Error("Invalid or expired OTP");
    }
}

export async function confirmPasswordChange(
    oldPassword: string,
    newPassword: string,
    confirmPassword: string,
    token: string
): Promise<void> {
    try {
        const response = await axios.post(
            ENDPOINT_USER_CONFIRM_PASSWORD_CHANGE,
            {
                old_password: oldPassword,
                new_password: newPassword,
                confirm_password: confirmPassword,
                token: token
            },
            { withCredentials: true }
        );
        console.log(response.data);

        if (response.data.Code !== "200") {
            throw new Error(response.data.Msg || "Failed to change password");
        }
    } catch (err: any) {
        if (err.response?.data?.Msg) {
            throw new Error(err.response.data.Msg);
        }
        throw new Error("Failed to change password");
    }
}

export interface SecurityEvent {
    event: string;
    userid: string;
    username: string;
    ip: string;
    user_agent: string;
    outcome: string;
    timestamp: number;
}

// Retrieves the signed-in user's recent security activity (audit log), newest first.
export async function getSecurityActivity(): Promise<SecurityEvent[]> {
    try {
        const response = await axios.post(
            ENDPOINT_USER_SECURITY_ACTIVITY,
            {},
            { withCredentials: true }
        );

        if (response.data.Code !== "200") {
            throw new Error(response.data.Msg || "Failed to load security activity");
        }

        return (response.data.Msg as SecurityEvent[]) || [];
    } catch (err: any) {
        if (err.response?.data?.Msg) {
            throw new Error(err.response.data.Msg);
        }
        throw new Error("Failed to load security activity");
    }
}

export async function requestDeleteAccount(password: string): Promise<void> {
    try {
        const response = await axios.post(
            ENDPOINT_USER_DELETE_SELF,
            { password: btoa(password) },
            { withCredentials: true }
        );

        if (response.data.Code !== "200") {
            throw new Error(response.data.Msg || "Failed to delete account");
        }
    } catch (err: any) {
        if (err.response?.data?.Msg) {
            throw new Error(err.response.data.Msg);
        }
        throw new Error(err.message || "Failed to delete account");
    }
}
